import React, { useState, useEffect, useCallback } from "react";
import { Plus, Trash2, AlertCircle, Loader2, ChevronDown, ChevronUp, ChevronsDownUp, ChevronsUpDown } from "lucide-react";
import IOSSwitch from "../../Common/IOSSwitch";
import ConfirmationDialog from "../../Common/ConfirmationDialog";
import {
    listOptionGroups,
    createOptionGroup,
    updateOptionGroup,
    deleteOptionGroup,
} from "./optionGroupsService";
import AppleSelect from "../../Common/AppleSelect";

const genKey = () => Date.now() + Math.random();

// Normalize an option-group from the API (snake_case) to the UI shape (camelCase).
const fromApiGroup = (group) => ({
    id: group.id,
    _key: group.id || genKey(),
    name: group.name || "",
    selectionType: group.selection_type || "single",
    required: !!group.required,
    freeQuantity: group.free_quantity ?? 0,
    sortOrder: group.sort_order ?? 0,
    options: (group.options || []).map((option) => ({
        id: option.id,
        name: option.name || "",
        priceDelta: option.price_delta ?? 0,
        sortOrder: option.sort_order ?? 0,
        active: option.active !== false,
    })),
});

const emptyOption = (sortOrder) => ({
    name: "",
    priceDelta: 0,
    sortOrder,
    active: true,
});

const emptyGroup = (sortOrder) => ({
    _key: genKey(),
    name: "",
    selectionType: "single",
    required: false,
    freeQuantity: 0,
    sortOrder,
    options: [emptyOption(0)],
});

/**
 * Manages the option groups (Personnalisation des plats) of a product or a
 * product variant (déclinaison). Rendered as a tab/section inside
 * EditProductForm and EditVarianteProductForm for every commerce type — labeled
 * "Options" for Restaurant accounts, "Packs" for everyone else (`groupLabel`
 * drives the wording used across this component). Pass either `productId` or
 * `variantId` (variant takes precedence if both are given).
 */
const ProductOptionGroups = ({ productId, variantId, groupLabel = "groupe", onNotify }) => {
    const entityType = variantId ? "variants" : "products";
    const entityId = variantId || productId;
    const GroupLabel = groupLabel.charAt(0).toUpperCase() + groupLabel.slice(1);

    const [groups, setGroups] = useState([]);
    const [loading, setLoading] = useState(true);
    const [savingId, setSavingId] = useState(null); // group id (or "new") being saved
    const [error, setError] = useState(null);
    // Per-group collapse state, keyed by the group's stable `_key` (all expanded by default).
    const [collapsedKeys, setCollapsedKeys] = useState(() => new Set());
    // Index of the group pending delete confirmation (null when no dialog is open).
    const [groupToDelete, setGroupToDelete] = useState(null);
    // { groupIndex, optionIndex } of the option pending delete confirmation.
    const [optionToDelete, setOptionToDelete] = useState(null);

    const notify = useCallback(
        (type, message) => {
            if (onNotify) onNotify(type, message);
        },
        [onNotify]
    );

    const fetchGroups = useCallback(async () => {
        try {
            setLoading(true);
            setError(null);
            const data = await listOptionGroups(entityType, entityId);
            setGroups((data || []).map(fromApiGroup));
        } catch (err) {
            console.error("Failed to load option groups:", err);
            setError(`Impossible de charger les ${groupLabel}s d'options.`);
        } finally {
            setLoading(false);
        }
    }, [entityType, entityId]);

    useEffect(() => {
        if (entityId) fetchGroups();
    }, [entityId, fetchGroups]);

    // --- Local edit helpers -------------------------------------------------

    const updateGroupField = (index, field, value) => {
        setGroups((prev) =>
            prev.map((g, i) => (i === index ? { ...g, [field]: value } : g))
        );
    };

    const updateOptionField = (groupIndex, optionIndex, field, value) => {
        setGroups((prev) =>
            prev.map((g, i) => {
                if (i !== groupIndex) return g;
                const options = g.options.map((o, oi) =>
                    oi === optionIndex ? { ...o, [field]: value } : o
                );
                return { ...g, options };
            })
        );
    };

    const addOption = (groupIndex) => {
        setGroups((prev) =>
            prev.map((g, i) =>
                i === groupIndex
                    ? { ...g, options: [...g.options, emptyOption(g.options.length)] }
                    : g
            )
        );
    };

    const removeOption = (groupIndex, optionIndex) => {
        setGroups((prev) =>
            prev.map((g, i) =>
                i === groupIndex
                    ? { ...g, options: g.options.filter((_, oi) => oi !== optionIndex) }
                    : g
            )
        );
    };

    const addGroup = () => {
        setGroups((prev) => [...prev, emptyGroup(prev.length)]);
    };

    // Swap a group with its neighbour and immediately persist the new
    // sort_order for both (groups that are already saved server-side).
    const moveGroup = async (index, direction) => {
        const target = index + direction;
        if (target < 0 || target >= groups.length) return;

        const reordered = [...groups];
        [reordered[index], reordered[target]] = [reordered[target], reordered[index]];
        const withOrder = reordered.map((g, i) => ({ ...g, sortOrder: i }));
        setGroups(withOrder);

        const moved = [withOrder[index], withOrder[target]].filter((g) => g.id);
        if (moved.length === 0) return;
        try {
            await Promise.all(moved.map((g) => updateOptionGroup(entityType, entityId, g.id, g)));
        } catch (err) {
            console.error("Failed to persist group order:", err);
            notify("error", `Échec de l'enregistrement de l'ordre des ${groupLabel}s.`);
            fetchGroups();
        }
    };

    // Swap an option with its neighbour within the same group and persist
    // the change (unless the group hasn't been saved yet).
    const moveOption = async (groupIndex, optionIndex, direction) => {
        const group = groups[groupIndex];
        const target = optionIndex + direction;
        if (target < 0 || target >= group.options.length) return;

        const options = [...group.options];
        [options[optionIndex], options[target]] = [options[target], options[optionIndex]];
        const updatedGroup = { ...group, options: options.map((o, oi) => ({ ...o, sortOrder: oi })) };
        setGroups((prev) => prev.map((g, i) => (i === groupIndex ? updatedGroup : g)));

        if (!group.id) return;
        try {
            setSavingId(group.id);
            const saved = await updateOptionGroup(entityType, entityId, group.id, updatedGroup);
            setGroups((prev) => prev.map((g, i) => (i === groupIndex ? fromApiGroup(saved) : g)));
        } catch (err) {
            console.error("Failed to persist option order:", err);
            notify("error", "Échec de l'enregistrement de l'ordre des options.");
            fetchGroups();
        } finally {
            setSavingId(null);
        }
    };

    const toggleGroupCollapsed = useCallback((key) => {
        setCollapsedKeys((prev) => {
            const next = new Set(prev);
            if (next.has(key)) next.delete(key);
            else next.add(key);
            return next;
        });
    }, []);

    const allGroupsCollapsed = groups.length > 0 && groups.every((g) => collapsedKeys.has(g._key));
    const toggleCollapseAll = () => {
        setCollapsedKeys(allGroupsCollapsed ? new Set() : new Set(groups.map((g) => g._key)));
    };

    // --- Persistence --------------------------------------------------------

    const validateGroup = (group) => {
        if (!group.name?.trim()) return `Le nom du ${groupLabel} est requis.`;
        const validOptions = group.options.filter((o) => o.name?.trim());
        if (validOptions.length === 0) return "Ajoutez au moins une option nommée.";
        return null;
    };

    const saveGroup = async (index) => {
        const group = groups[index];
        const validationError = validateGroup(group);
        if (validationError) {
            notify("error", validationError);
            return;
        }

        // Re-number sort_order from the current visual order, drop empty options.
        const payload = {
            ...group,
            sortOrder: index,
            options: group.options
                .filter((o) => o.name?.trim())
                .map((o, oi) => ({ ...o, sortOrder: oi })),
        };

        try {
            setSavingId(group.id || "new");
            const saved = group.id
                ? await updateOptionGroup(entityType, entityId, group.id, payload)
                : await createOptionGroup(entityType, entityId, payload);
            const normalized = fromApiGroup(saved);
            setGroups((prev) => prev.map((g, i) => (i === index ? normalized : g)));
            notify("success", `${GroupLabel} « ${normalized.name} » enregistré.`);
        } catch (err) {
            console.error("Failed to save option group:", err);
            if (err?.response?.status === 403) {
                notify("error", "Vous n'avez pas les droits pour configurer ces options.");
            } else {
                notify("error", err?.response?.data?.error || `Échec de l'enregistrement du ${groupLabel}.`);
            }
        } finally {
            setSavingId(null);
        }
    };

    const removeGroup = async (index) => {
        const group = groups[index];
        // Unsaved group: just drop it locally.
        if (!group.id) {
            setGroups((prev) => prev.filter((_, i) => i !== index));
            return;
        }
        try {
            setSavingId(group.id);
            await deleteOptionGroup(entityType, entityId, group.id);
            setGroups((prev) => prev.filter((_, i) => i !== index));
            notify("success", `${GroupLabel} « ${group.name} » supprimé.`);
        } catch (err) {
            console.error("Failed to delete option group:", err);
            if (err?.response?.status === 403) {
                notify("error", "Vous n'avez pas les droits pour configurer ces options.");
            } else {
                notify("error", `Échec de la suppression du ${groupLabel}.`);
            }
        } finally {
            setSavingId(null);
        }
    };

    const confirmRemoveGroup = async () => {
        const index = groupToDelete;
        setGroupToDelete(null);
        if (index !== null) await removeGroup(index);
    };

    const confirmRemoveOption = () => {
        const target = optionToDelete;
        setOptionToDelete(null);
        if (target) removeOption(target.groupIndex, target.optionIndex);
    };

    // --- Render -------------------------------------------------------------

    if (loading) {
        return (
            <div className="flex items-center justify-center py-12 text-gray-500 dark:text-gray-400">
                <Loader2 className="w-5 h-5 animate-spin mr-2" />
                Chargement des options…
            </div>
        );
    }

    if (error) {
        return (
            <div className="flex flex-col items-center justify-center py-12 gap-3">
                <div className="flex items-center text-red-600 dark:text-red-400">
                    <AlertCircle className="w-5 h-5 mr-2" />
                    {error}
                </div>
                <button
                    type="button"
                    onClick={fetchGroups}
                    className="text-sm text-primary hover:underline"
                >
                    Réessayer
                </button>
            </div>
        );
    }

    return (
        <div className="space-y-4 sm:space-y-6">
            <div className="flex flex-col sm:flex-row sm:items-start justify-between gap-3">
                <p className="text-xs sm:text-sm text-gray-500 dark:text-gray-400 max-w-2xl">
                    Configurez les {groupLabel}s d'options proposés à la vente (ex. « Cuisson »,
                    « Suppléments »). Chaque option peut ajouter un supplément de prix. Pour un choix
                    multiple, vous pouvez rendre les premières sélections gratuites et facturer les
                    suivantes (ex. 2 sauces gratuites, puis 5 DH la sauce supplémentaire).
                </p>
                <div className="w-full sm:w-auto flex items-center gap-2">
                    {groups.length > 0 && (
                        <button
                            type="button"
                            onClick={toggleCollapseAll}
                            className="shrink-0 inline-flex items-center gap-1.5 px-3 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 text-xs sm:text-sm font-medium text-gray-600 dark:text-gray-300 bg-gray-100 dark:bg-gray-700 rounded-lg hover:bg-gray-200 dark:hover:bg-gray-600 transition-colors"
                        >
                            {allGroupsCollapsed
                                ? <ChevronsUpDown className="w-4 h-4" />
                                : <ChevronsDownUp className="w-4 h-4" />}
                            {allGroupsCollapsed ? "Tout déplier" : "Tout replier"}
                        </button>
                    )}
                    <button
                        type="button"
                        onClick={addGroup}
                        className="flex-1 sm:flex-none shrink-0 inline-flex items-center justify-center gap-1.5 px-3 py-2.5 sm:py-2 min-h-[44px] sm:min-h-0 text-xs sm:text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 transition-colors"
                    >
                        <Plus className="w-4 h-4" />
                        Ajouter un {groupLabel}
                    </button>
                </div>
            </div>

            {groups.length === 0 && (
                <div className="text-center py-12 text-gray-500 dark:text-gray-400 border border-dashed border-gray-300 dark:border-gray-700 rounded-xl">
                    Aucun {groupLabel} d'options. Cliquez sur « Ajouter un {groupLabel} » pour commencer.
                </div>
            )}

            {groups.map((group, groupIndex) => {
                const isSaving = savingId === (group.id || "new");
                const collapsed = collapsedKeys.has(group._key);
                const open = !collapsed;
                return (
                    <div
                        key={group.id || `new-${groupIndex}`}
                        className="rounded-xl border border-gray-200 dark:border-gray-700 bg-white dark:bg-gray-800 overflow-hidden"
                    >
                        {/* Collapsible header */}
                        <button
                            type="button"
                            onClick={() => toggleGroupCollapsed(group._key)}
                            className="w-full flex flex-wrap items-center justify-between gap-x-3 gap-y-2 px-4 sm:px-5 py-3 bg-gray-50 dark:bg-gray-900/30 hover:bg-gray-100 dark:hover:bg-gray-900/50 transition-colors text-left"
                        >
                            <span className="flex items-center gap-2 min-w-0 max-w-full font-medium text-sm text-gray-800 dark:text-gray-100">
                                <span className="truncate">
                                    {group.name || `Nouveau ${groupLabel}`}
                                </span>
                                {group.required && (
                                    <span className="flex-shrink-0 text-xs text-red-500">*obligatoire</span>
                                )}
                                {group.selectionType === "multiple" && Number(group.freeQuantity) > 0 && (
                                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-amber-100 text-amber-700 dark:bg-amber-900/40 dark:text-amber-300">
                                        {group.freeQuantity} gratuite{group.freeQuantity > 1 ? "s" : ""}
                                    </span>
                                )}
                                {group.options.length > 0 && (
                                    <span className="flex-shrink-0 px-1.5 py-0.5 rounded-full text-[11px] font-semibold bg-primary/10 text-primary">
                                        {group.options.length} option{group.options.length > 1 ? "s" : ""}
                                    </span>
                                )}
                            </span>
                            <div className="flex items-center gap-1 flex-shrink-0 ml-auto">
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); moveGroup(groupIndex, -1); }}
                                    disabled={isSaving || groupIndex === 0}
                                    className="p-1.5 sm:p-1 text-gray-400 hover:text-primary rounded disabled:opacity-30 disabled:hover:text-gray-400"
                                    aria-label={`Monter le ${groupLabel}`}
                                >
                                    <ChevronUp className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); moveGroup(groupIndex, 1); }}
                                    disabled={isSaving || groupIndex === groups.length - 1}
                                    className="p-1.5 sm:p-1 text-gray-400 hover:text-primary rounded disabled:opacity-30 disabled:hover:text-gray-400"
                                    aria-label={`Descendre le ${groupLabel}`}
                                >
                                    <ChevronDown className="w-4 h-4" />
                                </button>
                                <button
                                    type="button"
                                    onClick={(e) => { e.stopPropagation(); setGroupToDelete(groupIndex); }}
                                    disabled={isSaving}
                                    className="p-1.5 sm:p-1 ml-1 text-red-400 hover:text-red-600 rounded disabled:opacity-50"
                                    aria-label={`Supprimer le ${groupLabel}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </button>
                                <ChevronDown
                                    className={`w-4 h-4 text-gray-400 transition-transform duration-200 shrink-0 ${open ? "rotate-180" : ""}`}
                                />
                            </div>
                        </button>

                        <div className={`grid transition-[grid-template-rows] duration-200 ease-in-out ${open ? "grid-rows-[1fr]" : "grid-rows-[0fr]"}`}>
                        <div className="overflow-hidden">
                        <div className="p-4 sm:p-5 space-y-4">
                        {/* Group header */}
                        <div className="grid grid-cols-1 sm:grid-cols-2 gap-3 sm:gap-4">
                            <div className="sm:col-span-2">
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                    Nom du {groupLabel}
                                </label>
                                <input
                                    type="text"
                                    value={group.name}
                                    onChange={(e) => updateGroupField(groupIndex, "name", e.target.value)}
                                    placeholder="Ex. Cuisson, Suppléments…"
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                />
                            </div>
                            <div>
                                <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                    Type de sélection
                                </label>
                                <AppleSelect
                                    value={group.selectionType}
                                    onChange={(e) => updateGroupField(groupIndex, "selectionType", e.target.value)}
                                    className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                >
                                    <option value="single">Choix unique</option>
                                    <option value="multiple">Choix multiple</option>
                                </AppleSelect>
                            </div>
                            <div className="flex items-end pb-1">
                                <div className="flex items-center gap-3">
                                    <IOSSwitch
                                        label="Obligatoire"
                                        checked={group.required}
                                        onChange={(val) => updateGroupField(groupIndex, "required", val)}
                                    />
                                    <span className="text-sm text-gray-700 dark:text-gray-200">
                                        Obligatoire
                                    </span>
                                </div>
                            </div>
                            {group.selectionType === "multiple" && (
                                <div className="sm:col-span-2">
                                    <label className="block text-xs font-medium text-gray-600 dark:text-gray-300 mb-1">
                                        Nombre d'options gratuites
                                    </label>
                                    <input
                                        type="number"
                                        step="1"
                                        min="0"
                                        value={group.freeQuantity}
                                        onChange={(e) => updateGroupField(
                                            groupIndex,
                                            "freeQuantity",
                                            e.target.value === "" ? "" : Math.max(0, Number(e.target.value))
                                        )}
                                        placeholder="Ex. 2"
                                        className="w-full sm:w-40 px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                    />
                                    <p className="mt-1 text-xs text-gray-500 dark:text-gray-400">
                                        Les {group.freeQuantity || 0} premières options choisies sont gratuites.
                                        Au-delà, chaque option supplémentaire est facturée à son propre supplément
                                        de prix. Laissez 0 pour facturer chaque option dès la première.
                                    </p>
                                </div>
                            )}
                        </div>

                        {/* Options */}
                        <div className="space-y-2">
                            <div className="hidden sm:grid grid-cols-12 gap-2 px-1 text-xs font-medium text-gray-500 dark:text-gray-400">
                                <span className="col-span-5">Option</span>
                                <span className="col-span-3">Supplément (DH)</span>
                                <span className="col-span-2">Active</span>
                                <span className="col-span-2" />
                            </div>
                            {group.options.map((option, optionIndex) => (
                                <div
                                    key={option.id || `opt-${optionIndex}`}
                                    className="grid grid-cols-12 gap-2 items-center"
                                >
                                    <div className="col-span-12 sm:col-span-5">
                                        <input
                                            type="text"
                                            value={option.name}
                                            onChange={(e) => updateOptionField(groupIndex, optionIndex, "name", e.target.value)}
                                            placeholder="Nom de l'option"
                                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                        />
                                    </div>
                                    <div className="col-span-4 sm:col-span-3">
                                        <input
                                            type="number"
                                            step="0.01"
                                            min="0"
                                            value={option.priceDelta}
                                            onChange={(e) => updateOptionField(groupIndex, optionIndex, "priceDelta", e.target.value === "" ? "" : Number(e.target.value))}
                                            className="w-full px-3 py-2 text-sm rounded-lg border border-gray-300 dark:border-gray-600 bg-white dark:bg-gray-700 text-gray-900 dark:text-white focus:ring-2 focus:ring-primary focus:border-transparent outline-none"
                                        />
                                    </div>
                                    <div className="col-span-4 sm:col-span-2 flex items-center">
                                        <IOSSwitch
                                            label="Active"
                                            checked={option.active}
                                            onChange={(val) => updateOptionField(groupIndex, optionIndex, "active", val)}
                                        />
                                    </div>
                                    <div className="col-span-4 sm:col-span-2 flex items-center justify-end gap-0.5">
                                        <button
                                            type="button"
                                            onClick={() => moveOption(groupIndex, optionIndex, -1)}
                                            disabled={optionIndex === 0}
                                            className="p-1.5 sm:p-1 text-gray-400 hover:text-primary rounded disabled:opacity-30 disabled:hover:text-gray-400"
                                            aria-label="Monter l'option"
                                        >
                                            <ChevronUp className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => moveOption(groupIndex, optionIndex, 1)}
                                            disabled={optionIndex === group.options.length - 1}
                                            className="p-1.5 sm:p-1 text-gray-400 hover:text-primary rounded disabled:opacity-30 disabled:hover:text-gray-400"
                                            aria-label="Descendre l'option"
                                        >
                                            <ChevronDown className="w-4 h-4" />
                                        </button>
                                        <button
                                            type="button"
                                            onClick={() => setOptionToDelete({ groupIndex, optionIndex })}
                                            className="p-1.5 sm:p-1 ml-1 text-gray-400 hover:text-red-500 transition-colors"
                                            aria-label="Supprimer l'option"
                                        >
                                            <Trash2 className="w-4 h-4" />
                                        </button>
                                    </div>
                                </div>
                            ))}
                            <button
                                type="button"
                                onClick={() => addOption(groupIndex)}
                                className="inline-flex items-center gap-1 text-xs sm:text-sm text-primary hover:underline mt-1"
                            >
                                <Plus className="w-3.5 h-3.5" />
                                Ajouter une option
                            </button>
                        </div>

                        {/* Group actions */}
                        <div className="flex items-center justify-end pt-2 border-t border-gray-100 dark:border-gray-700">
                            <button
                                type="button"
                                onClick={() => saveGroup(groupIndex)}
                                disabled={isSaving}
                                className="inline-flex items-center gap-1.5 px-4 py-2 text-sm font-medium text-white bg-primary rounded-lg hover:bg-primary/90 disabled:opacity-50 transition-colors"
                            >
                                {isSaving && <Loader2 className="w-4 h-4 animate-spin" />}
                                {group.id ? "Mettre à jour" : "Enregistrer"}
                            </button>
                        </div>
                        </div>
                        </div>
                        </div>
                    </div>
                );
            })}

            {groupToDelete !== null && (
                <ConfirmationDialog
                    title={`Supprimer le ${groupLabel}`}
                    message={`Voulez-vous vraiment supprimer le ${groupLabel} « ${groups[groupToDelete]?.name || `Nouveau ${groupLabel}`} » ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    danger
                    onConfirm={confirmRemoveGroup}
                    onCancel={() => setGroupToDelete(null)}
                />
            )}

            {optionToDelete !== null && (
                <ConfirmationDialog
                    title="Supprimer l'option"
                    message={`Voulez-vous vraiment supprimer l'option « ${groups[optionToDelete.groupIndex]?.options[optionToDelete.optionIndex]?.name || ""} » ? Cette action est irréversible.`}
                    confirmLabel="Supprimer"
                    cancelLabel="Annuler"
                    danger
                    onConfirm={confirmRemoveOption}
                    onCancel={() => setOptionToDelete(null)}
                />
            )}
        </div>
    );
};

export default ProductOptionGroups;
